import React, { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import {
  faPlusCircle,
  faTimesCircle,
  faSearch,
  faCheckCircle
} from '@fortawesome/pro-solid-svg-icons';
import { Classes } from '@blueprintjs/core';
import { Form, FormInstance, TextInput } from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import HighlightText from '../../atoms/text/highlight';
import Button from '../../molecules/button';
import Card, { CardBlock } from '../../organisms/card';
import Pagination from '../../organisms/pagination';
import { LoadingBlock } from '../../organisms/loading/progress';
import BrandSelect from '../../organisms/brand/select';
import { useMetaData } from '../../../contexts/metadata';
import { GetThread, GetThreadResponse } from '../../../libs/chat';
import { GetFullName, IPatient, useGetPatients } from '../../../libs/patient';
import { FormatPhone } from '../../../libs/format';
import { useGetBrands } from '../../../libs/brand';
import { usePatientGroup } from '../../../contexts/patient-group';

// Select Patient

interface SelectPatientProps {
  setPatient: (patient: IPatient, brandId: number) => any;
  startAddingPatient: () => any;
  close: () => any;
  externalKeyword?: string;
  setExternalKeyword: (value: React.SetStateAction<string>) => void;
}

interface SimpleSearchData {
  keyword: string;
}

const PAGE_LIMIT = 8;

interface SelectPatientCardProps {
  patient: IPatient;
  isSelected: boolean;
  selectPatient?: (patient: IPatient) => void;
  keyword?: string;
  confirmSettings?: (brandId: string) => void;
  isForChat?: boolean;
  startChat?: (patient: IPatient, brandId: number) => void;
}

export const SelectPatientCard = ({
  patient,
  isSelected,
  selectPatient,
  keyword,
  confirmSettings,
  isForChat = false,
  startChat
}: SelectPatientCardProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [thread, setThread] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const { isLoading: isLoadingBrands, brands } = useGetBrands({
    page: 0,
    limit: 20
  });
  const showBrandSelect = !thread && brands?.length > 0;
  const disableButton =
    brands?.length > 0 &&
    (!selectedBrandId.length || selectedBrandId === '0') &&
    !thread;
  const renderActions = () => {
    if (!isSelected) {
      return null;
    }
    if (isLoading || isLoadingBrands) {
      return (
        <Button className="mt-6" intent="success" disabled fill>
          <Icon className="mr-2" icon={faCheckCircle} /> Loading...
        </Button>
      );
    }
    const buttonText = thread ? 'Go To Chat' : 'Start Chat';
    return (
      <React.Fragment>
        {showBrandSelect ? (
          <div className="mt-6">
            <BrandSelect
              onChange={(brand) => setSelectedBrandId(brand.id.toString())}
              isLoading={isLoadingBrands}
              brands={brands}
              value={selectedBrandId}
              fill={!isForChat} // Only fill if not for chat
              shouldAutoFit={isForChat} // Only auto fit if for chat
            />
          </div>
        ) : null}
        <Button
          className="mt-6"
          intent="success"
          disabled={disableButton}
          onClick={() =>
            isForChat
              ? startChat(patient, parseInt(selectedBrandId))
              : confirmSettings(selectedBrandId)
          }
          fill
        >
          <Icon className="mr-2" icon={faCheckCircle} /> {buttonText}
        </Button>
      </React.Fragment>
    );
  };

  useEffect(() => {
    let isRendered = true;
    // Check if already has a text thread
    if (isSelected) {
      setIsLoading(true);
      GetThread({
        patientId: patient.id
      }).then((response) => {
        if (isRendered) {
          setIsLoading(false);
          if (response.status !== 'OK') {
            // Do nothing on error at this time
            return;
          }
          const thread = (response as GetThreadResponse).thread;
          if (thread) {
            setThread(thread);
            const brandId = thread.brandId;
            if (brandId) {
              setSelectedBrandId(brandId.toString());
            }
          }
        }
      });
    }
    () => {
      isRendered = false;
    };
  }, [isSelected]);

  const content: JSX.Element = (
    <>
      <Row align="center">
        {!isForChat && (
          <>
            <Col xs={5}>
              <Button intent="success" disabled={isSelected} size="small" fill>
                <Icon className="mr-2" icon={faCheckCircle} /> Select
              </Button>
            </Col>
            <Col xs={19}>
              <b className="mr-3">
                {HighlightText(GetFullName(patient), keyword, 'highlight')}
              </b>
              <span className="ml-2">{FormatPhone(patient.phone)}</span>
              {patient.dob || patient.mrn ? (
                <React.Fragment>
                  <br />
                  <span className="text-small">
                    {patient.dob ? (
                      <>
                        <span className="uppercase font-bold">DOB</span>
                        <span className="ml-2">{patient.dob}</span>
                      </>
                    ) : null}
                    {patient.mrn ? (
                      <>
                        <span
                          className={`${patient.dob ? 'ml-6 ' : ''}uppercase font-bold`}
                        >
                          MRN
                        </span>
                        <span className="ml-2">{patient.mrn}</span>
                      </>
                    ) : null}
                  </span>
                </React.Fragment>
              ) : null}
            </Col>
          </>
        )}
      </Row>
      {renderActions()}
    </>
  );

  return isForChat ? (
    <>
      <div className="pr-4 pb-4 pl-4">{content}</div>
    </>
  ) : (
    <Card
      className="mb-4"
      interactive
      onClick={isForChat ? null : selectPatient?.bind(this, patient)}
    >
      {content}
    </Card>
  );
};

interface PatientListProps {
  isLoading: boolean;
  patients: IPatient[];
  chosenPatient: IPatient;
  selectPatient: (patient: IPatient) => void;
  keyword: string;
  confirmSettings: (brandId: string) => void;
}

const PatientList = ({
  isLoading,
  patients,
  chosenPatient,
  selectPatient,
  keyword,
  confirmSettings
}: PatientListProps): JSX.Element => {
  const { terms } = useMetaData();
  if (isLoading) {
    return <LoadingBlock />;
  }
  if (!patients || !patients.length) {
    return (
      <Card>
        <h3>No {terms.patient.pluralCapitalized}</h3>
        <p>
          There are no {terms.patient.plural} to display. Try searching by a
          different name or changing your {terms.patient.capitalized} View.
        </p>
      </Card>
    );
  }
  return (
    <React.Fragment>
      {patients.map((patient) => (
        <SelectPatientCard
          key={patient.id}
          patient={patient}
          isSelected={chosenPatient && chosenPatient.id === patient.id}
          selectPatient={selectPatient}
          keyword={keyword}
          confirmSettings={confirmSettings}
        />
      ))}
    </React.Fragment>
  );
};

const SelectPatient = ({
  close,
  setPatient,
  startAddingPatient,
  externalKeyword,
  setExternalKeyword
}: SelectPatientProps): JSX.Element => {
  const [keyword, setKeyword] = useState<string>(null);
  const [chosenPatient, setChosenPatient] = useState<IPatient>(null);
  const [page, setPage] = useState(1);

  // Fetch the current patient group
  const { isLoading: pgLoading, getActiveGroup } = usePatientGroup();

  // Use undefined here as a way to differentiate between
  // null, which is the 'All Patients View' and undefined,
  // which is 'not done fetching yet'
  const activePatientGroup = !pgLoading ? getActiveGroup() : undefined;

  const form = new FormInstance<SimpleSearchData>({
    keyword: ''
  });

  const clear = () => {
    setKeyword(null);
    setExternalKeyword(null);
    form.setData({
      keyword: ''
    });
  };

  const search = debounce((data: SimpleSearchData) => {
    const value = data.keyword === '' ? null : data.keyword;
    setKeyword(value);
    setExternalKeyword(value);
  }, 100);

  const { isLoading, patients, total } = useGetPatients(
    {
      name: keyword,
      limit: PAGE_LIMIT,
      page: page - 1,
      filters: activePatientGroup?.filters,
      includeArchived: true
    },
    !pgLoading
  );

  const selectPatient = (patient: IPatient) => {
    setChosenPatient(patient);
  };

  const confirmSettings = (brandId: string) => {
    if (!chosenPatient) {
      return;
    }
    const parsedBrandId = brandId ? parseInt(brandId) : null;
    setPatient(chosenPatient, parsedBrandId);
    close();
  };

  // Reset the page and other associated state when keyword changes
  useEffect(() => {
    setPage(1);
    setChosenPatient(null);
  }, [keyword]);

  // Upon render, use the other keyword if it exists
  useEffect(() => {
    if (externalKeyword) {
      setKeyword(externalKeyword);
      form.setFieldValue('keyword', externalKeyword);
    }
  }, []);

  return (
    <div className={Classes.DRAWER_BODY}>
      <CardBlock>
        <Form form={form} onSubmit={search}>
          <Row>
            <Col sm={10} md={14}>
              <TextInput
                label="Keyword"
                name="keyword"
                placeholder="Search by name"
                rightElement={
                  keyword ? (
                    <Button
                      variant="minimal"
                      icon={<Icon icon={faTimesCircle} />}
                      onClick={clear}
                    />
                  ) : null
                }
                bare
              />
            </Col>
            <Col sm={7} md={5}>
              <Button type="submit" intent="primary" fill>
                <Icon icon={faSearch} className="mr-2" /> Search
              </Button>
            </Col>
            <Col sm={7} md={5}>
              <Button intent="primary" fill onClick={startAddingPatient}>
                <Icon icon={faPlusCircle} className="mr-2" /> Add
              </Button>
            </Col>
          </Row>
        </Form>
      </CardBlock>
      <CardBlock>
        <PatientList
          isLoading={isLoading}
          patients={patients}
          keyword={keyword}
          chosenPatient={chosenPatient}
          confirmSettings={confirmSettings}
          selectPatient={selectPatient}
        />
        <Pagination
          page={page}
          total={total}
          limit={PAGE_LIMIT}
          onPageChange={(page) => setPage(page)}
        />
      </CardBlock>
    </div>
  );
};

export default SelectPatient;
